import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from 'primereact/button'

import { CustomConfirmDialog } from '../../../../../../components/dialogs/CustomConfirmDialog'
import {
  useNavBlockerContext,
  useToastContext,
} from '../../../../../../contexts'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { requestDeleteEInvoice, useEInvoiceById } from '../../../../data'

export const Delete: React.FC = () => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { setBlocker } = useNavBlockerContext()

  const { id: eInvoiceId } = useParams()
  const { data: eInvoice, isLoading } = useEInvoiceById(eInvoiceId!)
  const navigate = useNavigate()

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false)

  const handleDelete = async () => {
    setIsRequestRunning(true)

    setBlocker(false)

    try {
      await requestDeleteEInvoice(eInvoiceId!)

      showToast({
        severity: 'success',
        summary: t(
          'eInvoiceDetails.title.delete.dialog.delete.messages.success.summary',
        ),
        detail: t(
          'eInvoiceDetails.title.delete.dialog.delete.messages.success.detail',
          { invoiceNumber: eInvoice?.Payload?.ID.Value?.Value ?? '' },
        ),
      })

      setIsDialogVisible(false)

      navigate('/e-invoices')
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        severity: 'error',
        sticky: true,
        summary: t(
          'eInvoiceDetails.title.delete.dialog.delete.messages.failure.summary',
        ),
        detail: errorMessage,
      })

      setIsDialogVisible(false)
    } finally {
      setIsRequestRunning(false)
    }
  }

  const handleCancel = () => {
    if (!isRequestRunning) {
      setIsDialogVisible(false)
    }
  }

  const openDialog = () => {
    setIsDialogVisible(true)
  }

  return (
    <>
      <Button
        label={t('eInvoiceDetails.title.delete.label')}
        icon="pi pi-trash"
        outlined
        onClick={openDialog}
        loading={isRequestRunning}
      />
      <CustomConfirmDialog
        severity="danger"
        isVisible={isDialogVisible}
        header={t('eInvoiceDetails.title.delete.dialog.title')}
        message={t('eInvoiceDetails.title.delete.dialog.message', {
          invoiceNumber: eInvoice?.Payload?.ID.Value?.Value ?? '',
        })}
        rejectLabel={t('eInvoiceDetails.title.delete.dialog.cancel.label')}
        acceptLabel={t('eInvoiceDetails.title.delete.dialog.delete.label')}
        onHide={handleCancel}
        onReject={handleCancel}
        onAccept={handleDelete}
        closable={!isRequestRunning}
      />
    </>
  )
}
